import { computed, ref } from "vue";
import { defineStore } from 'pinia';
import type { Client } from "@/models/client";
import { ClientApiService } from "@/services/clientApiService";
import { useDebounceFn } from "@vueuse/core";
import type { MembershipTier } from "@/models/membershipTier";
import { MembershipTierApiService } from "@/services/membershipTierApiService";
import { MembershipModel } from "@/models/misc";
import type { ClientFeatureFlags } from "@/models/clientFeatureFlags";
import type { Destination } from "@/models/destination";

// Shared app state
export const useAppStore = defineStore('app', () => {

  const client = ref<Client | null>(null);
  const featureFlags = computed(() => client.value?.featureFlags ?? ({} as ClientFeatureFlags));
  const destinations = computed<Destination[]>(() => client.value?.destinations ?? []);
  const membershipTiers = ref<MembershipTier[]>([]);

  const isSubscriptionProgram = computed(() => {
    if (!client.value) {
      return false;
    }
    return client.value?.membershipModel === MembershipModel.SUBSCRIPTION
      || client.value?.membershipModel === MembershipModel.ROUND_UP_SUBSCRIPTION;
  });

  const isWhiteLabel = computed(() => {
    if (!client.value) {
      return false;
    }
    return client.value?.featureFlags.whiteLabeled ?? false;
  });

  const isNewOffersSystem = computed(() => {
    if (!client.value) {
      return false;
    }
    return client.value?.featureFlags.newOffersSystem ?? false;
  });

  const isDowngradeMembership = computed(() => {
    if (!client.value) {
      return false;
    }
    return client.value?.featureFlags.downgradeMembership ?? false;
  });

  const isAmexUxLimited = computed(() => {
    if (!client.value) {
      return false;
    }
    return client.value?.featureFlags.amexLimitUx ?? false;
  });

  const labelVersion = computed(() => {
    if (isWhiteLabel.value) {
      return 'whitelabel';
    } else if (isSubscriptionProgram.value) {
      return 'subscription';
    }

    return 'roundup';
  });

  /** Referral Section */
  const isReferralEnabled = computed(() => {
    if (featureFlags.value.newReferralSystem) {
      return !!client.value?.referralProgram;
    }
    return !!client.value?.referralProgramId;
  });

  const windowHeight = ref<number>(window.innerHeight);
  const windowWidth = ref<number>(window.innerWidth);
  const windowScrollY = ref<number>(window.scrollY);

  const showDuotoneBackground = ref<boolean>(false);
  const showDuotoneBackgroundDots = ref<boolean>(false);
  const showCardLinkModal = ref<boolean>(false);
  const showCardModal = ref<boolean>(false);
  const showReferralRockModal = ref<boolean>(false);
  const showMembershipUpgradeModal = ref<boolean>(false);
  const showCancelMembershipModal = ref<boolean>(false);
  const showSweepStakesModal = ref<boolean>(false);
  const showSweepStakesReminder = ref<boolean>(false);

  async function fetchClient() {
    if (client.value) {
      return;
    }
    const response = await ClientApiService.get();
    client.value = response.data?.client ?? null;
  }

  async function fetchMembershipTiers() {
    if (!client.value) {
      return;
    }

    const response = await MembershipTierApiService.get();
    membershipTiers.value = response.data?.membershipTiers ?? [];
  }

  function $reset() {
    showDuotoneBackground.value = false;
    showDuotoneBackgroundDots.value = false;
    showCardLinkModal.value = false;
    showCardModal.value = false;
    showReferralRockModal.value = false;
  }

  const onResize = useDebounceFn(() => {
    windowHeight.value = window.innerHeight;
    windowWidth.value = window.innerWidth;

    document.documentElement.style.setProperty('--inner-vh', `${window.innerHeight / 100}px`);
  }, 200);

  function onScroll() {
    windowScrollY.value = window.scrollY;
  }

  return {
    windowHeight,
    windowWidth,
    windowScrollY,
    onResize,
    onScroll,
    client,
    featureFlags,
    destinations,
    membershipTiers,
    isSubscriptionProgram,
    isAmexUxLimited,
    labelVersion,
    isWhiteLabel,
    isNewOffersSystem,
    isDowngradeMembership,
    // Referrals
    isReferralEnabled,
    showReferralRockModal,
    // ---
    showDuotoneBackground,
    showDuotoneBackgroundDots,
    showCardLinkModal,
    showCardModal,
    showSweepStakesModal,
    showSweepStakesReminder,
    showMembershipUpgradeModal,
    showCancelMembershipModal,
    fetchClient,
    fetchMembershipTiers,
    $reset,
  };
});
